import React, { useState } from 'react';
import { Navbar } from './Navbar';
import { Mail, Link, Copy, CheckCircle, ArrowLeft, Send, Sparkles } from 'lucide-react';
import './InviteFriend.css';

export const InviteFriend = ({ 
  analysisResult, 
  user, 
  onSignInClick, 
  onBack 
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inviteCreated, setInviteCreated] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [error, setError] = useState('');

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/invites/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invited_email: email.trim(),
          inviter_user_id: user?.id || null,
          inviter_test_results: analysisResult
        })
      });

      const data = await response.json();

      if (data.success) {
        setInviteCreated(true);
        setInviteLink(data.invite_link);
      } else {
        setError(data.error || 'Failed to create invite');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareViaEmail = () => {
    const subject = "This personality test is so accurate it's fun";
    const body = `I just took this fun personality test that analyzes your handwriting — and the results were so on point! Take it so we can compare — are we better as friends, lovers, or startup partners?.\n\nTake the test here: ${inviteLink}\n\nIt takes like 2 minutes. Let me know what you get!`;
    
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  if (inviteCreated) {
    return (
      <div className="invite-friend-page base-background">
        <Navbar currentUser={user} onSignInClick={onSignInClick} />
        
        <div className="ambient-bg">
          <div className="ambient-orb orb-purple"></div>
          <div className="ambient-orb orb-pink"></div>
        </div>

        <div className="invite-container">
          <div className="invite-success-section glass-card">
            <div className="success-icon-wrapper">
              <CheckCircle size={48} color="#10b981" />
            </div>
            <h2 className="branded-success-title">Invitation Ready</h2>
            <p className="success-subtitle">Share this link to start the sync.</p>
            
            <div className="invite-link-wrapper">
              <div className="link-input-pill">
                <Link size={14} className="link-icon" />
                <input 
                  type="text" 
                  value={inviteLink} 
                  readOnly 
                  className="minimal-link-input"
                />
                <button 
                  onClick={copyToClipboard}
                  className="copy-icon-button"
                  title="Copy to clipboard"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <div className="share-actions">
              <button 
                onClick={shareViaEmail}
                className="glass-cta share-btn"
                style={{ width: '100%' }}
              >
                <Send size={18} />
                <span className="cta-text">Send via Email</span>
              </button>
            </div>

            <div className="invite-meta-pill">
              <span>📧 {email}</span>
              <span className="dot"></span>
              <span>Expires in 7 days</span>
            </div>

            <button 
              onClick={onBack} 
              className="back-to-home-btn"
            >
              <ArrowLeft size={16} />
              Return to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-friend-page base-background">
      <Navbar currentUser={user} onSignInClick={onSignInClick} />
      
      <div className="ambient-bg">
        <div className="ambient-orb orb-purple"></div>
        <div className="ambient-orb orb-pink"></div>
      </div>

      <div className="invite-container">
        <div className="invite-form-section">
          <div className="invite-hero-header">
            <h1 className="headline" style={{ fontSize: '2rem' }}>
              Invite a <span className="headline-accent">Friend.</span>
            </h1>
            <p className="subheadline" style={{ fontSize: '1rem', opacity: 0.7 }}>
              Sync profiles and decode your group dynamic.
            </p>
          </div>

          <div className="mini-card-preview-wrapper">
             <div className="mini-card glass-card">
                <div className="mini-card-header">
                    <div className="mini-avatar">{(user?.fullName || 'U').charAt(0)}</div>
                    <div className="mini-info">
                        <span className="mini-name">Your Character Card</span>
                        <span className="mini-type">{(analysisResult.insights?.title || analysisResult.dominance || 'Explorer').toUpperCase()}</span>
                    </div>
                </div>
                <div className="mini-balance">
                    <div className="mini-balance-bar">
                        <div className="mini-balance-fill" style={{ width: `${analysisResult.left_percentage}%` }}></div>
                    </div>
                    <div className="mini-balance-labels">
                        <span>{analysisResult.left_percentage}% Logic</span>
                        <span>{analysisResult.right_percentage}% Intuition</span>
                    </div>
                </div>
             </div>
          </div>

          <form onSubmit={handleCreateInvite} className="modern-invite-form">
            <div className="glass-input-group">
              <label htmlFor="friend-email"><Mail size={12} /> Friend's Email</label>
              <div className="input-with-icon">
                <input
                  id="friend-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="friend@email.com"
                  className="glass-input"
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <div className="error-pill">{error}</div>
            )}

            <div className="cta-wrapper">
              <div className="cta-glow"></div>
              <button 
                type="submit" 
                className="glass-cta"
                style={{ width: '100%' }}
                disabled={isLoading}
              >
                {isLoading ? (
                    <div className="button-spinner"></div>
                ) : (
                    <>
                        <Sparkles size={18} />
                        <span className="cta-text">Generate Invite Link</span>
                    </>
                )}
              </button>
            </div>
          </form>

          <button 
            onClick={onBack} 
            className="secondary-glass-button"
            style={{ marginTop: '20px', width: '100%' }}
          >
            <ArrowLeft size={16} />
            Back to Analysis
          </button>
        </div>
      </div>
    </div>
  );
};